// Demo fixtures for the Call-to-Claim prototype: a realistic sample advice-call
// transcript and the canned AnswerSet the hybrid pipeline returns until the live
// Claude extraction is wired in (it produces the same shape).

import type { AnswerSet } from './lib/types';

export const SAMPLE_CLIENT = {
  name: 'Arthur Whitmore',
  audioFileName: 'arthur-whitmore-aa-call.m4a',
};

export const SAMPLE_TRANSCRIPT = `Adviser: Right, hello there, is that Diane?
Caller: Yes, speaking. Is that the lady I spoke to before, about my dad's allowance?
Adviser: That's me. So I'm going to help you fill in the Attendance Allowance form for your father today, the AA1. I'll ask you questions, you tell me what you can, and anything we can't answer we leave and come back to.
Caller: That's great. He's not here, he's at his own house, but I do most of his paperwork.
Adviser: Perfect. Let's start with his details. What's his full name?
Caller: Arthur Whitmore. And he's got one middle name, Edward. So Arthur Edward Whitmore. Everyone just calls him Arthur.
Adviser: And it's Mr? And his date of birth?
Caller: Yes, Mr. He was born the fourteenth of March, 1943. So he's eighty-two now.
Adviser: And Arthur is male. His address?
Caller: 27 Marlcliffe Road, in Sheffield, the Hillsborough area. The postcode is S6 4QB.
Adviser: A phone number for him?
Caller: His landline is 0114 285 6691, though it's better they ring me.
Adviser: He's British, and normally lives in Great Britain?
Caller: Yes, British, born in Sheffield, never lived anywhere else.
Adviser: And his National Insurance number?
Caller: It's QQ 12 34 56 C.
Adviser: Now his conditions. What illnesses or disabilities does Arthur have?
Caller: The main one is Parkinson's, diagnosed early 2020, so coming up six years. He's got osteoarthritis in his hips and hands, longstanding but much worse the last couple of years. And the doctors have said he's got early vascular dementia, only since about last September.
Adviser: And his medications?
Caller: Co-careldopa for the Parkinson's, the 25/100 ones, three times a day. Atorvastatin 20mg at night. Amlodipine 5mg in the morning. And paracetamol, with co-codamol on bad days.
Adviser: His GP and surgery?
Caller: Dr Okafor, at the Hillsborough Medical Centre, on Middlewood Road, Sheffield. I think the postcode is S6 1TD.
Adviser: Has he seen any other professionals in the last year?
Caller: A Parkinson's specialist nurse, Sandra Bennett, from the Royal Hallamshire. She came to the house in February.
Adviser: Let's go through the help he needs during the day. Getting in and out of bed?
Caller: Mornings are the worst, he's ever so stiff. Most mornings he needs a hand getting out of bed.
Adviser: Washing and bathing?
Caller: He can't get in the bath at all. He washes at the sink but someone has to be there, he can't do his back or reach his feet. Every day.
Adviser: Dressing?
Caller: Every day, morning and night. Buttons and socks are impossible with his hands and the shakes.
Adviser: Toilet and continence?
Caller: He's slow, he's had accidents, he wears pads now, and he needs reminding to go because of the dementia.
Adviser: Meals?
Caller: He can feed himself but he can't cut anything up, someone has to cut it all up every meal, and remind him to keep eating.
Adviser: Medication?
Caller: He'd not take them at all without someone reminding him, three or four times a day.
Adviser: Moving around indoors and stairs?
Caller: He uses a frame, he freezes in doorways, and he needs someone with him on the stairs. He's had a few falls, one bad one last summer that ended in A&E.
Adviser: Night-time, does he need help?
Caller: He can't turn himself over in bed, and he needs the toilet at night. Two to three times a night, ten to fifteen minutes each time, longer if he's confused.
Adviser: Does he need someone keeping an eye on him during the day?
Caller: Definitely. He left the hob on once, the kitchen filled with smoke. And he's wandered off and got confused about where he was. I wouldn't leave him more than an hour.
Adviser: Is he currently in hospital or a care home?
Caller: No, he's at home.
Adviser: And he's not terminally ill, not claiming under the special rules?
Caller: No, nothing like that.
Adviser: Last thing, his bank details for payment?
Caller: Ah, I haven't got them on me, they're in the folder at his house. I'll get them to you.
Adviser: No problem, we'll leave the bank details to follow and I'll add them before it's submitted.`;

const q = (
  questionId: string,
  value: AnswerSet[string]['value'],
  confidence: AnswerSet[string]['confidence'],
  sourceQuotes: string[],
): [string, AnswerSet[string]] => [
  questionId,
  { questionId, value, confidence, sourceQuotes },
];

/** Canned extraction result for the sample call (the live pipeline returns this shape). */
export const CANNED_ANSWERS: AnswerSet = Object.fromEntries([
  q('attendance-allowance-surname', 'Whitmore', 'high', ['Arthur Whitmore']),
  q('attendance-allowance-other-names', 'Arthur Edward', 'high', [
    'Arthur Edward Whitmore',
  ]),
  q('attendance-allowance-title', 'Mr', 'high', ['Yes, Mr.']),
  q('attendance-allowance-national-insurance', 'QQ123456C', 'high', [
    "It's QQ 12 34 56 C.",
  ]),
  q('attendance-allowance-date-of-birth', '1943-03-14', 'high', [
    'born the fourteenth of March, 1943',
  ]),
  q('attendance-allowance-sex', 'attendance-allowance-male', 'high', [
    'Arthur is male',
  ]),
  q(
    'attendance-allowance-address',
    {
      addressLine1: '27 Marlcliffe Road',
      addressLine2: 'Hillsborough',
      city: 'Sheffield',
      postalCode: 'S6 4QB',
    },
    'high',
    [
      '27 Marlcliffe Road, in Sheffield, the Hillsborough area. The postcode is S6 4QB.',
    ],
  ),
  q('attendance-allowance-phone-number', '0114 285 6691', 'medium', [
    'His landline is 0114 285 6691',
  ]),
  q('attendance-allowance-nationality', 'nationality-british', 'high', [
    'Yes, British',
  ]),
  q(
    'attendance-allowance-normally-live-gb',
    'attendance-allowance-yes',
    'high',
    ['normally lives in Great Britain'],
  ),
  q('attendance-allowance-special-rules', false, 'high', [
    'No, nothing like that.',
  ]),
  q('care-recipient-any-illnesses-or-disabilities', true, 'high', [
    "The main one is Parkinson's",
  ]),
  q(
    'attendance-allowance-what-illnesses-or-disabilities',
    [
      {
        'attendance-allowance-illnesses-or-disabilities-name-of-illness':
          "Parkinson's disease",
        'attendance-allowance-illnesses-or-disabilities-how-long':
          'Diagnosed early 2020 (about 6 years)',
        'attendance-allowance-illnesses-or-disabilities-medicines':
          'Co-careldopa',
        'attendance-allowance-illnesses-or-disabilities-dosage':
          '25/100, three times a day',
      },
      {
        'attendance-allowance-illnesses-or-disabilities-name-of-illness':
          'Osteoarthritis (hips and hands)',
        'attendance-allowance-illnesses-or-disabilities-how-long':
          'Longstanding, worse in the last 2 years',
        'attendance-allowance-illnesses-or-disabilities-medicines':
          'Paracetamol; co-codamol as needed',
        'attendance-allowance-illnesses-or-disabilities-dosage':
          'As required on bad days',
      },
      {
        'attendance-allowance-illnesses-or-disabilities-name-of-illness':
          'Early vascular dementia',
        'attendance-allowance-illnesses-or-disabilities-how-long':
          'Since about September last year',
        'attendance-allowance-illnesses-or-disabilities-medicines':
          'None reported',
        'attendance-allowance-illnesses-or-disabilities-dosage': 'None',
      },
    ],
    'high',
    [
      "The main one is Parkinson's, diagnosed early 2020",
      'osteoarthritis in his hips and hands',
      'early vascular dementia, only since about last September',
    ],
  ),
  // --- GP and care history -------------------------------------------------
  q('attendance-allowance-gp-name', 'Dr Okafor', 'high', [
    'Dr Okafor, at the Hillsborough Medical Centre',
  ]),
  q(
    'attendance-allowance-gp-full-address',
    {
      addressLine1: 'Hillsborough Medical Centre',
      addressLine2: 'Middlewood Road',
      city: 'Sheffield',
      postalCode: 'S6 1TD',
    },
    'high',
    [
      'at the Hillsborough Medical Centre, on Middlewood Road, Sheffield. I think the postcode is S6 1TD.',
    ],
  ),
  q('attendance-allowance-date-started-to-need-help', '2024-06-01', 'low', [
    'much worse the last couple of years',
  ]),

  // --- Help needed during the day ------------------------------------------
  q('attendance-allowance-care-needs-bed', true, 'high', [
    'Most mornings he needs a hand getting out of bed.',
  ]),
  q(
    'attendance-allowance-care-needs-help-bed',
    ['attendance-allowance-help-getting-out-of-bed'],
    'high',
    ['Most mornings he needs a hand getting out of bed.'],
  ),
  q('attendance-allowance-bed-additional', true, 'medium', [
    "Mornings are the worst, he's ever so stiff.",
  ]),
  q(
    'attendance-allowance-care-needs-bed-additional-details',
    "I am very stiff first thing in the morning because of my Parkinson's and arthritis. Most mornings I cannot get out of bed without someone helping me up.",
    'medium',
    [
      "Mornings are the worst, he's ever so stiff.",
      'Most mornings he needs a hand getting out of bed.',
    ],
  ),

  q('attendance-allowance-care-needs-toilet', true, 'high', [
    "he's had accidents, he wears pads now, and he needs reminding to go",
  ]),
  q('attendance-allowance-care-needs-toilet-help', 4, 'medium', [
    "He's slow, he's had accidents, he wears pads now",
  ]),
  q('attendance-allowance-care-needs-incontinence-help', 2, 'medium', [
    'he wears pads now',
  ]),
  q('attendance-allowance-care-needs-toilet-motivation', 3, 'medium', [
    'he needs reminding to go because of the dementia',
  ]),
  q('attendance-allowance-care-needs-toilet-additional', true, 'medium', [
    'he needs reminding to go because of the dementia',
  ]),
  q(
    'attendance-allowance-care-needs-toilet-additional-details',
    'I am slow getting to the toilet and have had accidents, so I now wear incontinence pads which need changing. Because of my dementia I do not remember to go and need reminding through the day.',
    'medium',
    [
      "He's slow, he's had accidents, he wears pads now, and he needs reminding to go because of the dementia.",
    ],
  ),

  q('attendance-allowance-care-needs-cleaning', true, 'high', [
    "someone has to be there, he can't do his back or reach his feet. Every day.",
  ]),
  q('attendance-allowance-care-needs-bath-difficulty', 1, 'high', [
    "He can't get in the bath at all.",
  ]),
  q('attendance-allowance-care-needs-hygiene-help', 1, 'high', [
    'He washes at the sink but someone has to be there',
    'Every day.',
  ]),
  q('attendance-allowance-care-needs-hygiene-additional', true, 'medium', [
    "he can't do his back or reach his feet",
  ]),
  q(
    'attendance-allowance-care-needs-hygiene-additional-details',
    'I cannot get into the bath at all, so I wash at the sink every day. Someone has to be with me because I cannot wash my back or reach my feet.',
    'medium',
    [
      "He can't get in the bath at all. He washes at the sink but someone has to be there, he can't do his back or reach his feet.",
    ],
  ),

  q('attendance-allowance-care-needs-dressing', true, 'high', [
    'Buttons and socks are impossible with his hands and the shakes.',
  ]),
  q('attendance-allowance-care-needs-clothes-fastening-difficulty', 2, 'high', [
    'Every day, morning and night.',
  ]),
  q('attendance-allowance-care-needs-clothes-fastening-help', '2', 'high', [
    'Every day, morning and night.',
  ]),
  q(
    'attendance-allowance-care-needs-dressing-difficulty-additional',
    true,
    'medium',
    ['Buttons and socks are impossible'],
  ),
  q(
    'attendance-allowance-care-needs-dressing-additional-details',
    'My hands shake and are stiff with arthritis, so buttons, fastenings and socks are impossible for me. I need help dressing every morning and undressing every night.',
    'medium',
    [
      'Every day, morning and night. Buttons and socks are impossible with his hands and the shakes.',
    ],
  ),

  q('attendance-allowance-moving-around-indoors-difficulty', true, 'high', [
    'He uses a frame, he freezes in doorways',
  ]),
  q(
    'attendance-allowance-care-needs-moving-around-indoors-difficulties',
    [
      'attendance-allowance-difficulty-walking-around-indoors',
      'attendance-allowance-difficulty-going-up-or-down-stairs',
    ],
    'high',
    [
      'He uses a frame, he freezes in doorways, and he needs someone with him on the stairs.',
    ],
  ),
  q(
    'attendance-allowance-care-needs-during-help-moving-around-indoors',
    [
      'attendance-allowance-help-walking-around-indoors',
      'attendance-allowance-help-going-up-or-down-stairs',
    ],
    'high',
    ['he needs someone with him on the stairs'],
  ),
  q(
    'attendance-allowance-care-needs-difficulty-moving-around-indoors-additional',
    true,
    'medium',
    ['he freezes in doorways'],
  ),
  q(
    'attendance-allowance-care-needs-difficulty-moving-around-indoors-additional-details',
    "I walk indoors with a frame and my Parkinson's makes me freeze in doorways. I cannot manage the stairs without someone with me.",
    'medium',
    [
      'He uses a frame, he freezes in doorways, and he needs someone with him on the stairs.',
    ],
  ),

  q(
    'attendance-allowance-fall-or-stumble-illnesses-or-disabilities',
    ['attendance-allowance-fall'],
    'high',
    ["He's had a few falls, one bad one last summer that ended in A&E."],
  ),
  q(
    'attendance-allowance-care-needs-what-happens-when-fall-or-stumble',
    "I freeze and lose my balance because of my Parkinson's. I have had several falls, including a bad one last summer that ended in A&E.",
    'high',
    ["He's had a few falls, one bad one last summer that ended in A&E."],
  ),
  q('attendance-allowance-when-last-fall', '2025-07-15', 'low', [
    'one bad one last summer that ended in A&E',
  ]),

  q(
    'attendance-allowance-difficulty-cutting-up-food-eating-or-drinking',
    true,
    'high',
    ["he can't cut anything up, someone has to cut it all up every meal"],
  ),
  q('attendance-allowance-cutting-up-food-help-how-often', 3, 'high', [
    'someone has to cut it all up every meal',
  ]),
  q('attendance-allowance-eat-or-drink-motivation-difficulty', 3, 'medium', [
    'remind him to keep eating',
  ]),
  q('attendance-allowance-eating-or-drinking-additional', true, 'medium', [
    'remind him to keep eating',
  ]),
  q(
    'attendance-allowance-eating-or-drinking-additional-details',
    'I can feed myself but my hands cannot manage a knife, so someone has to cut all my food up at every meal. Because of my dementia I also need reminding to keep eating during the meal.',
    'medium',
    [
      "He can feed himself but he can't cut anything up, someone has to cut it all up every meal, and remind him to keep eating.",
    ],
  ),

  q('attendance-allowance-medication-difficulty', true, 'high', [
    "He'd not take them at all without someone reminding him",
  ]),
  q('attendance-allowance-medication-motivation-how-often', 3, 'high', [
    'three or four times a day',
  ]),
  q('attendance-allowance-medication-additional', true, 'medium', [
    "He'd not take them at all without someone reminding him",
  ]),
  q(
    'attendance-allowance-medication-additional-details',
    'I take co-careldopa three times a day plus other tablets, and because of my memory I would not take any of them without someone prompting me each time.',
    'medium',
    [
      "He'd not take them at all without someone reminding him, three or four times a day.",
    ],
  ),

  q('attendance-allowance-care-needs-days-per-week', '7', 'medium', [
    'Every day.',
    'three times a day',
  ]),

  // --- Supervision during the day -------------------------------------------
  q('attendance-allowance-need-someone-supervision', true, 'high', [
    'He left the hob on once',
    "he's wandered off and got confused",
  ]),
  q(
    'attendance-allowance-need-someone-supervision-select',
    [
      'attendance-allowance-supervision-prevent-danger',
      'attendance-allowance-supervision-wander',
      'attendance-allowance-supervision-confusion',
    ],
    'high',
    [
      'He left the hob on once, the kitchen filled with smoke.',
      "he's wandered off and got confused about where he was",
    ],
  ),
  q(
    'attendance-allowance-how-long-left-alone-safely',
    'About an hour',
    'high',
    ["I wouldn't leave him more than an hour."],
  ),
  q('attendance-allowance-supervision-additional', true, 'medium', [
    'He left the hob on once, the kitchen filled with smoke.',
  ]),
  q(
    'attendance-allowance-supervision-additional-details',
    'I once left the hob on and the kitchen filled with smoke. I have also wandered off and become confused about where I was. My family will not leave me on my own for more than about an hour.',
    'medium',
    [
      'He left the hob on once, the kitchen filled with smoke.',
      "he's wandered off and got confused about where he was. I wouldn't leave him more than an hour.",
    ],
  ),
  q('attendance-allowance-supervision-days-a-week', '7', 'medium', [
    "I wouldn't leave him more than an hour.",
  ]),

  // --- Help needed during the night -----------------------------------------
  q('attendance-allowance-care-needs-night-difficulty', true, 'high', [
    "He can't turn himself over in bed, and he needs the toilet at night.",
  ]),
  q(
    'attendance-allowance-care-needs-night-getting-into-bed-difficulty',
    'attendance-allowance-turning-over-two',
    'medium',
    ["He can't turn himself over in bed"],
  ),
  q(
    'attendance-allowance-night-toilet-needs-difficulty-how-often',
    'attendance-allowance-night-toilet-needs-difficulty-two',
    'high',
    ['Two to three times a night'],
  ),
  q(
    'attendance-allowance-night-toilet-needs-difficulty-how-many-minutes',
    15,
    'high',
    ["ten to fifteen minutes each time, longer if he's confused"],
  ),
  q('attendance-allowance-night-difficulty-additional', true, 'medium', [
    "longer if he's confused",
  ]),
  q(
    'attendance-allowance-night-difficulty-additional-details',
    'I cannot turn over or change position in bed without help. I need the toilet two or three times every night, ten to fifteen minutes each time, and longer when I am confused.',
    'medium',
    [
      "He can't turn himself over in bed, and he needs the toilet at night. Two to three times a night, ten to fifteen minutes each time, longer if he's confused.",
    ],
  ),
  q('attendance-allowance-night-difficulty-how-many-nights', '7', 'medium', [
    'Two to three times a night',
  ]),

  // --- Anything else / hospital ---------------------------------------------
  q(
    'attendance-allowance-care-needs-additional',
    "My needs are getting worse. My Parkinson's, arthritis and dementia together mean I need help or supervision through the whole day and night, every day of the week.",
    'medium',
    [
      'much worse the last couple of years',
      'early vascular dementia, only since about last September',
    ],
  ),
  q('attendance-allowance-in-hospital-or-care-home-right-now', false, 'high', [
    "No, he's at home.",
  ]),
  q(
    'attendance-allowance-come-out-of-hospital-past-six-weeks',
    false,
    'medium',
    ["No, he's at home.", 'one bad one last summer that ended in A&E'],
  ),
]);
